/// <reference path="../../../types/table-metadata.d.ts" />
import {
  ColumnDef,
  useReactTable,
  flexRender,
  getCoreRowModel,
  Row,
  ExpandedState
} from '@tanstack/react-table'
import { Fragment, ReactNode, useCallback, useState } from 'react'
import {
  FaChevronDown,
  FaChevronRight,
  FaChevronUp,
  FaSort
} from 'react-icons/fa'

import {
  SortParamValues,
  useSort
} from 'src/providers/SortProvider/SortProvider'

export type TableData = {
  id: string
}

export type TableProps<T extends object> = {
  cellClasses?: string | ((columnAccessor: string) => string)
  headerClasses?: string | ((columnAccessor: string) => string)
  subComponentClasses?: string
  className?: string
  columns: ColumnDef<T, string>[]
  data: T[]
  isSelectable?: (item: T) => boolean
  onSelect?: (item: T, row: Row<T>) => void
  renderSubComponent?: ({
    row,
    onRowUpdated
  }: {
    row: Row<T>
    onRowUpdated?: (data: T) => void
  }) => ReactNode
  rowClasses?: string | ((item: T) => string)
  showHeader?: boolean
  noResultsText?: string | JSX.Element
  enableSorting?: boolean
  showSelectChevron?: boolean
  showSortIndicator?: boolean
}

export const Table = <T extends object>({
  cellClasses = '',
  headerClasses = '',
  subComponentClasses = '',
  className = '',
  columns,
  data,
  isSelectable,
  onSelect,
  renderSubComponent,
  rowClasses = '',
  showHeader = true,
  noResultsText = 'No results',
  enableSorting = true,
  showSelectChevron = false,
  showSortIndicator = false
}: TableProps<T>) => {
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const { setSort, sort } = useSort()

  const table = useReactTable<T>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableExpanding: true,
    onExpandedChange: setExpanded,
    state: {
      expanded
    }
  })

  const onRowClick = useCallback(
    (row: Row<T>) => {
      if (isSelectable) {
        return isSelectable(row.original) && onSelect
          ? onSelect(row.original, row)
          : null
      }
      return onSelect ? onSelect(row.original, row) : null
    },
    [onSelect, isSelectable]
  )

  const isRowSelectable = useCallback(
    (row: Row<T>) => {
      if (isSelectable) {
        return onSelect && isSelectable(row.original)
      }
      return onSelect
    },
    [onSelect, isSelectable]
  )

  const onHeaderClick = (identifier: string) => {
    const getSortState = (columnName: string): SortParamValues => {
      // Find the column definition to check for default sort direction
      const column = columns.find(col => {
        if ('accessorKey' in col) return col.accessorKey === columnName
        if ('id' in col) return col.id === columnName
        return false
      })
      const defaultDirection = column?.meta?.defaultSortDirection || 'asc'

      if (!sort) {
        return {
          sortColumn: identifier,
          sortOrder: defaultDirection
        }
      }
      const { sortOrder, sortColumn } = sort
      if (sortColumn === columnName) {
        const newDirection = sortOrder === 'asc' ? 'desc' : 'asc'
        return { sortColumn, sortOrder: newDirection }
      }
      return { sortColumn: columnName, sortOrder: defaultDirection }
    }

    const newSortState = getSortState(identifier)

    setSort({
      sortColumn: newSortState.sortColumn as string,
      sortOrder: newSortState.sortOrder
    })
  }

  const getPinnedPosition = (
    columnDef: ColumnDef<T, unknown>
  ): React.CSSProperties | undefined => {
    const pinned = columnDef.meta?.pinned
    if (!pinned) {
      return undefined
    }
    if (pinned === 'left')
      return {
        position: 'sticky',
        left: 0
      }
    return {
      position: 'sticky',
      right: 0
    }
  }

  const getHeaderTextColor = ({
    enableSorting: isColumnSortingEnabled = true
  }: ColumnDef<T, unknown>): string => {
    if (!enableSorting) {
      return 'text-black'
    }
    if (enableSorting && !isColumnSortingEnabled) {
      return 'text-steelGrey'
    }
    return 'text-black'
  }

  return (
    <div className='w-full overflow-x-auto '>
      <table
        {...{
          style: {
            width: table.getCenterTotalSize()
          }
        }}
        className={`table-fixed w-full! px-5 py-3 mt-8 ${className}`}
      >
        {showHeader ? (
          <thead>
            {!!data.length && (
              <>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => {
                      const headerDef = header.column.columnDef.header
                      const hasHeader = headerDef !== '' && headerDef != null
                      const canSort =
                        enableSorting &&
                        hasHeader &&
                        header.getContext().column.getCanSort()
                      const isSortedColumn =
                        header.column.id === sort?.sortColumn

                      const getSortIcon = () => {
                        if (!canSort)
                          return <span className='ml-2 inline-block w-4' />
                        if (isSortedColumn)
                          return sort.sortOrder === 'asc' ? (
                            <FaChevronDown className='w-4 ml-2 inline-block text-forestDeep' />
                          ) : (
                            <FaChevronUp className='w-4 ml-2 inline-block text-forestDeep' />
                          )
                        return showSortIndicator ? (
                          <FaSort className='w-4 ml-2 inline-block text-forestDeep/40' />
                        ) : (
                          <span className='ml-2 inline-block w-4' />
                        )
                      }
                      const contentAlign =
                        header.column.columnDef.meta?.align || 'left'

                      const colSize = header.column.getSize()

                      return (
                        <th
                          style={{
                            width: colSize ? `${colSize}px` : 'auto',
                            ...getPinnedPosition(header.column.columnDef)
                          }}
                          onClick={() => {
                            if (canSort) onHeaderClick(header.id)
                          }}
                          key={header.id}
                          className={`bg-white ${getHeaderTextColor(
                            header.column.columnDef
                          )} ${
                            canSort
                              ? ' cursor-pointer hover:text-forestDeepHover'
                              : ''
                          } pb-2 px-2 text-${contentAlign} border-b border-solid border-gray-200 ${
                            typeof headerClasses === 'function'
                              ? headerClasses(header.id)
                              : headerClasses
                          }`}
                        >
                          {header.isPlaceholder ? null : (
                            <div
                              className={`flex flex-row ${
                                // eslint-disable-next-line no-nested-ternary
                                contentAlign === 'right'
                                  ? 'justify-end'
                                  : contentAlign === 'left'
                                  ? 'justify-start'
                                  : 'justify-center'
                              } items-center`}
                            >
                              <span className='inline-block'>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </span>
                              <span>{getSortIcon()}</span>
                            </div>
                          )}
                        </th>
                      )
                    })}

                    {showSelectChevron ? (
                      // eslint-disable-next-line jsx-a11y/control-has-associated-label
                      <th className='hidden lg:table-cell w-5 border-b border-solid border-gray-200' />
                    ) : null}
                  </tr>
                ))}
              </>
            )}
          </thead>
        ) : null}
        <tbody>
          {!data.length ? (
            <tr>
              <td className='text-center font-medium py-4'>{noResultsText}</td>
            </tr>
          ) : (
            table.getRowModel().rows.map(row => (
              <Fragment key={row.id}>
                <tr
                  key={row.id}
                  onClick={() => onRowClick(row)}
                  className={`group ${
                    typeof rowClasses === 'function'
                      ? rowClasses(row.original)
                      : rowClasses
                  } ${
                    isRowSelectable(row)
                      ? `bg-white hover:bg-frostWhite cursor-pointer`
                      : ''
                  }`}
                >
                  {row.getVisibleCells().map(cell => {
                    const contentAlign =
                      cell.column.columnDef.meta?.align || 'left'
                    const colSize = cell.column.getSize()

                    return (
                      <td
                        style={{
                          width: colSize ? `${colSize}px` : 'auto',
                          ...getPinnedPosition(cell.column.columnDef)
                        }}
                        key={cell.id}
                        className={`bg-inherit text-ellipsis overflow-hidden whitespace-normal ${
                          row.getVisibleCells().length > 1
                            ? 'pl-2 pr-8 py-4'
                            : 'pt-0 pb-4'
                        } ${
                          cell.row.getIsExpanded() ? '' : 'border-b'
                        } border-solid text-${contentAlign} ${
                          typeof cellClasses === 'function'
                            ? cellClasses(cell.column.id)
                            : cellClasses
                        }`}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    )
                  })}
                  {isRowSelectable(row) && showSelectChevron ? (
                    // eslint-disable-next-line jsx-a11y/control-has-associated-label
                    <td className='hidden lg:table-cell w-5 transition-all duration-300 group-hover:translate-x-1 border-b border-solid border-gray-200'>
                      <FaChevronRight
                        className='text-shark'
                        width={20}
                        height={20}
                      />
                    </td>
                  ) : null}
                </tr>
                {row.getIsExpanded() && renderSubComponent ? (
                  <tr>
                    {/* 2nd row is a custom 1 cell row */}
                    <td
                      colSpan={row.getVisibleCells().length}
                      className={`px-2 py-6 border-b border-solid border-gray-200 text-center ${subComponentClasses}`}
                    >
                      {renderSubComponent({ row })}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
